import { environment } from "../../../environments/environment";
import { KeycloakService } from "keycloak-angular";
import { REFRESH_TOKEN_KEY, TOKEN_KEY } from "./token-storage.service";

// check-sso waits for a silent-SSO iframe message that never arrives if Keycloak is unreachable.
const INIT_TIMEOUT_MS = 15000;

/** Route of the Bussola editor test page, which exists only in builds that are not production (see app.routes.ts). */
export const E2E_HARNESS_ROUTE = 'dev/bussola-content-editor-e2e';

/**
 * The e2e harness runs without a login, so Keycloak is not started for it. Only where the page exists: in a production
 * build the address is an unknown one, and skipping Keycloak there would just wipe the stored session of whoever opens it.
 */
export const isE2eHarnessPath = (pathname: string, isProduction: boolean): boolean =>
  !isProduction && pathname.startsWith(`/${E2E_HARNESS_ROUTE}`);

export const initializeKeycloak = (keycloak: KeycloakService) => {
  return async () => {
    if (isE2eHarnessPath(window.location.pathname, environment.IS_PRODUCTION)) {
      console.debug('[Keycloak] Skipping init for local e2e harness');
      return;
    }

    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    console.debug('[Keycloak] Init from localStorage:', {
      hasAccessToken: !!token,
      hasRefreshToken: !!refreshToken
    });

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      const initTimeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error(`Keycloak init timed out after ${INIT_TIMEOUT_MS} ms`)),
          INIT_TIMEOUT_MS
        );
      });

      await Promise.race([
        keycloak.init({
          config: {
            url: environment.KEYCLOAK_URL,
            realm: environment.KEYCLOAK_REALM,
            clientId: environment.KEYCLOAK_CLIENT_ID,
          },
          initOptions: {
            onLoad: 'check-sso',
            silentCheckSsoRedirectUri:
              window.location.origin + '/assets/silent-check-sso.html',
            checkLoginIframe: false,
            ...(token ? { token } : {}),
            ...(refreshToken ? { refreshToken } : {})
          },
          enableBearerInterceptor: false,
        }),
        initTimeout
      ]);
      console.debug('[Keycloak] Init complete — isLoggedIn:', keycloak.isLoggedIn());
    } catch (error) {
      console.warn('[Keycloak] Initialization error:', error);
    } finally {
      clearTimeout(timeoutId);
    }
  };
};
